import React, { useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiRefreshCw, FiSearch, FiUser, FiXCircle } from 'react-icons/fi';
import { useNotification } from '../context/NotificationContext';
import { AdminKycService, AdminKycCaseItem, AdminKycDocumentStatus } from '../services/adminKyc.service';
import styles from './AdminKycReviewPage.module.css';

const REQUIRED_DOC_TYPES = ['INE_FRONT', 'INE_BACK', 'ADDRESS_PROOF'] as const;

const statusMeta = (status?: string) => {
  const key = (status || '').toUpperCase();
  if (key === 'KYC_VALIDADO') return { label: 'Validado', className: styles.statusApproved, icon: FiCheckCircle };
  if (key === 'KYC_RECHAZADO') return { label: 'Rechazado', className: styles.statusRejected, icon: FiXCircle };
  if (key === 'KYC_EN_REVISION') return { label: 'En revisión', className: styles.statusReview, icon: FiClock };
  if (key === 'KYC_EN_PROCESO') return { label: 'En proceso', className: styles.statusPending, icon: FiClock };
  return { label: 'Pendiente', className: styles.statusPending, icon: FiClock };
};

const docStatusMeta = (status?: string) => {
  const key = (status || '').toUpperCase();
  if (key === 'APPROVED') return { label: 'Validado', className: styles.docApproved };
  if (key === 'REJECTED') return { label: 'Rechazado', className: styles.docRejected };
  if (key === 'PENDING') return { label: 'Pendiente', className: styles.docPending };
  return { label: 'Faltante', className: styles.docMissing };
};

const docLabel = (docType: string) => {
  if (docType === 'INE_FRONT') return 'INE frente';
  if (docType === 'INE_BACK') return 'INE reverso';
  if (docType === 'ADDRESS_PROOF') return 'Comprobante de domicilio';
  return docType;
};

const getUserInitials = (name: string | undefined, email: string | undefined, userId: number) => {
  const raw = (name || email || `U${userId}`).trim();
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  return raw.slice(0, 2).toUpperCase();
};

const formatDateLabel = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
};

const summarizeCaseDocs = (item: AdminKycCaseItem) => {
  const docs = Array.isArray(item.documents) ? item.documents : [];
  const requiredDocs = REQUIRED_DOC_TYPES.map((docType) => docs.find((doc) => doc.docType === docType));
  const uploaded = requiredDocs.filter(Boolean).length;
  const approved = requiredDocs.filter((doc) => (doc?.status || '').toUpperCase() === 'APPROVED').length;
  const rejected = requiredDocs.filter((doc) => (doc?.status || '').toUpperCase() === 'REJECTED').length;
  const pending = requiredDocs.filter((doc) => (doc?.status || '').toUpperCase() === 'PENDING').length;
  const missing = Math.max(REQUIRED_DOC_TYPES.length - uploaded, 0);
  const lastUploadAt = requiredDocs
    .map((doc) => doc?.uploadedAt)
    .filter((uploadedAt): uploadedAt is string => Boolean(uploadedAt))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  return {
    uploaded,
    approved,
    rejected,
    pending,
    missing,
    lastUploadAt,
  };
};

export default function AdminKycReviewPage() {
  const { addNotification } = useNotification();
  const [cases, setCases] = useState<AdminKycCaseItem[]>([]);
  const [selected, setSelected] = useState<AdminKycCaseItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMeta, setPreviewMeta] = useState<{ name?: string; mimeType?: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_URL as string;

  const loadCases = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AdminKycService.listCases({
        q: query.trim() || undefined,
        status: statusFilter || undefined,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setCases(items);
      if (items.length > 0 && !selected) setSelected(items[0]);
    } catch (err: any) {
      setCases([]);
      setSelected(null);
      setError(err?.message || 'API de revisión KYC pendiente de integración.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const orderedCases = useMemo(() => {
    if (!selected) return cases;
    const selectedIndex = cases.findIndex((item) => item.userId === selected.userId);
    if (selectedIndex <= 0) return cases;
    return [cases[selectedIndex], ...cases.slice(0, selectedIndex), ...cases.slice(selectedIndex + 1)];
  }, [cases, selected]);

  const selectedDocs = useMemo<AdminKycDocumentStatus[]>(() => selected?.documents || [], [selected]);

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewMeta(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const openPreview = async (doc: AdminKycDocumentStatus) => {
    if (!doc.fileUrl) {
      addNotification('Documento sin archivo asociado.', 'info');
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewUrl(null);
    setPreviewMeta({ name: doc.originalName, mimeType: doc.mimeType });

    try {
      const token = localStorage.getItem('auth_token') || '';
      const res = await fetch(`${apiBase}${doc.fileUrl}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      if (!doc.mimeType && blob.type) {
        setPreviewMeta((prev) => ({ ...(prev || {}), mimeType: blob.type }));
      }
    } catch (err: any) {
      setPreviewError(err?.message || 'No se pudo cargar la vista previa.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRunWorkflow = async () => {
    if (!selected) return;
    try {
      await AdminKycService.runWorkflow(selected.userId);
      addNotification('KYC validado temporalmente. El usuario ya puede continuar el flujo.', 'success');
      const detail = await AdminKycService.getCase(selected.userId).catch(() => null);
      if (detail?.case) {
        setSelected(detail.case);
        setCases((prev) => prev.map((item) => (item.userId === detail.case?.userId ? detail.case : item)));
      } else {
        await loadCases();
      }
    } catch (err: any) {
      addNotification(err?.message || 'No se pudo validar el KYC.', 'error');
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Administración</p>
          <h1 className={styles.title}>Revisión KYC</h1>
          <p className={styles.subtitle}>Revisa expedientes, valida documentos y dispara la verificación externa.</p>
        </div>
        <button className={styles.refreshButton} type="button" onClick={loadCases} disabled={loading}>
          <FiRefreshCw /> {loading ? 'Actualizando...' : 'Actualizar'}
        </button>
      </header>

      <section className={styles.filters}>
        <div className={styles.searchField}>
          <FiSearch />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por usuario o correo"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="KYC_PENDIENTE">Pendiente</option>
          <option value="KYC_EN_PROCESO">En proceso</option>
          <option value="KYC_EN_REVISION">En revisión</option>
          <option value="KYC_VALIDADO">Validado</option>
          <option value="KYC_RECHAZADO">Rechazado</option>
        </select>
        <button className={styles.filterButton} type="button" onClick={loadCases} disabled={loading}>
          Aplicar filtros
        </button>
      </section>

      {error ? <div className={styles.errorBanner}>{error}</div> : null}

      <div className={styles.contentGrid}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Expedientes</h2>
          {cases.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No hay expedientes disponibles.</p>
              <span>Conecta las APIs de revisión KYC para ver resultados aquí.</span>
            </div>
          ) : (
            <div className={styles.list}>
              {orderedCases.map((item) => {
                const meta = statusMeta(item.status);
                const StatusIcon = meta.icon;
                const docSummary = summarizeCaseDocs(item);
                const lastUpdate = formatDateLabel(item.updatedAt || docSummary.lastUploadAt);
                return (
                  <button
                    key={item.userId}
                    type="button"
                    className={`${styles.listItem} ${selected?.userId === item.userId ? styles.listItemActive : ''}`}
                    onClick={() => setSelected(item)}
                  >
                    <div className={styles.listItemMain}>
                      <div className={styles.avatar}>
                        {getUserInitials(item.name, item.email, item.userId)}
                      </div>
                      <div className={styles.listBody}>
                        <div className={styles.listTopRow}>
                          <div className={styles.listTitle}>{item.name || `Usuario #${item.userId}`}</div>
                          <span className={`${styles.statusBadge} ${meta.className}`}>
                            <StatusIcon size={14} /> {meta.label}
                          </span>
                        </div>
                        <div className={styles.listSubtitle}>{item.email}</div>
                        <div className={styles.listMetaRow}>
                          <span className={styles.metaChip}>Cargados {docSummary.uploaded}/3</span>
                          <span className={styles.metaChip}>Validados {docSummary.approved}</span>
                          {docSummary.pending > 0 ? (
                            <span className={styles.metaChip}>Pendientes {docSummary.pending}</span>
                          ) : null}
                          {docSummary.rejected > 0 ? (
                            <span className={`${styles.metaChip} ${styles.metaChipRejected}`}>
                              Rechazados {docSummary.rejected}
                            </span>
                          ) : null}
                          {docSummary.missing > 0 ? (
                            <span className={`${styles.metaChip} ${styles.metaChipMissing}`}>
                              Faltantes {docSummary.missing}
                            </span>
                          ) : null}
                          <span className={styles.listDate}>
                            {lastUpdate ? `Actualizado ${lastUpdate}` : 'Sin actividad reciente'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Detalle de expediente</h2>
          {!selected ? (
            <div className={styles.emptyState}>
              <p>Selecciona un expediente para revisar.</p>
            </div>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <div className={styles.detailUser}>
                  <FiUser />
                  <div>
                    <div className={styles.detailName}>{selected.name || `Usuario #${selected.userId}`}</div>
                    <div className={styles.detailEmail}>{selected.email}</div>
                  </div>
                </div>
                <span className={`${styles.statusBadge} ${statusMeta(selected.status).className}`}>
                  {statusMeta(selected.status).label}
                </span>
              </div>

              <div className={styles.docsGrid}>
                {REQUIRED_DOC_TYPES.map((docType) => {
                  const doc = selectedDocs.find((d) => d.docType === docType);
                  const meta = docStatusMeta(doc?.status);
                  return (
                    <div key={docType} className={styles.docCard}>
                      <div className={styles.docTitle}>{docLabel(docType)}</div>
                      <span className={`${styles.docStatus} ${meta.className}`}>{meta.label}</span>
                      <div className={styles.docMeta}>
                        {doc?.uploadedAt ? `Última carga: ${new Date(doc.uploadedAt).toLocaleString('es-MX')}` : 'Sin carga'}
                      </div>
                      {doc?.originalName ? <div className={styles.docNotes}>{doc.originalName}</div> : null}
                      {doc?.notes && !doc?.originalName ? <div className={styles.docNotes}>{doc.notes}</div> : null}
                      <button
                        type="button"
                        className={styles.previewButton}
                        onClick={() => doc && openPreview(doc)}
                        disabled={!doc?.fileUrl || previewLoading}
                      >
                        Ver vista previa
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className={styles.actionPanel}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleRunWorkflow}
                  disabled={!selected || loading}
                >
                  Validar KYC temporalmente
                </button>
                <p className={styles.actionHint}>
                  Validación temporal local mientras se integran Incode y SCISA.
                </p>
              </div>
            </>
          )}
        </section>
      </div>

      {previewUrl || previewLoading || previewError ? (
        <div className={styles.previewOverlay} onClick={closePreview}>
          <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.previewHeader}>
              <div>
                <div className={styles.previewTitle}>Vista previa</div>
                <div className={styles.previewSubtitle}>{previewMeta?.name || 'Documento KYC'}</div>
              </div>
              <button className={styles.previewClose} type="button" onClick={closePreview}>
                Cerrar
              </button>
            </div>

            <div className={styles.previewBody}>
              {previewLoading ? (
                <div className={styles.previewLoading}>Cargando documento...</div>
              ) : previewError ? (
                <div className={styles.previewError}>{previewError}</div>
              ) : previewUrl ? (
                previewMeta?.mimeType === 'application/pdf' ? (
                  <iframe title="Vista previa PDF" className={styles.previewFrame} src={previewUrl} />
                ) : (
                  <img className={styles.previewImage} src={previewUrl} alt={previewMeta?.name || 'Documento'} />
                )
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
